import { forwardRef, useMemo } from 'react';
import {
    IconHistory,
    type ListBoxItem,
    Select,
    SelectTrigger,
    type SelectTriggerProps,
    type TestProps,
} from '@direct-frontend/components';
import { castRef, useToggle } from '@direct-frontend/stdlib';

export type VersionSelectProps = Omit<SelectTriggerProps, 'size' | 'value'> &
    TestProps & {
        /** Пункты выпадающего списка (value — идентификатор версии в домене вызывающего кода). */
        items: ListBoxItem<string, string>[];
        /** Выбранное значение (`value` одного из `items`). */
        value: string;
        /** Выбор другого значения из списка. */
        onChange: (nextValue: string) => void;
        /** Подпись для триггера и списка (a11y). */
        label?: string;
    };

export const VersionSelect = (props: VersionSelectProps) => {
    const {
        items,
        value,
        onChange,
        label = 'История версий',
        'data-testid': dataTestId = 'VersionSelect',
        ...selectTriggerProps
    } = props;
    const { value: isSelectOpen, toggleOn: openSelect, toggleOff: closeSelect } = useToggle(false);

    const Trigger = useMemo(
        () =>
            forwardRef<HTMLElement>(function RenderTrigger(props, forwardedRef) {
                return (
                    <div ref={castRef(forwardedRef, 'input')} onClick={openSelect}>
                        <SelectTrigger
                            {...props}
                            data-testid={dataTestId}
                            aria-label={label}
                            isOpened={isSelectOpen}
                            onClick={isSelectOpen ? closeSelect : openSelect}
                            value={[
                                {
                                    value: 'value',
                                    content: label,
                                    iconLeft: <IconHistory size="16" color="gray" role="main" />,
                                    label,
                                },
                            ]}
                            size="s"
                            {...selectTriggerProps}
                        />
                    </div>
                );
            }),
        [openSelect, isSelectOpen, closeSelect, dataTestId, label, selectTriggerProps]
    );

    return (
        <Select
            data-testid={dataTestId}
            value={value}
            aria-label={label}
            items={items}
            size="s"
            color="contour"
            onChange={onChange}
            isOpen={isSelectOpen}
            onOpen={openSelect}
            onClose={closeSelect}
            renderTrigger={Trigger}
            placement="bottom-end"
            popupWidth={320}
        />
    );
};
